/**
 * Phase 20 — the amber banner while a campaign group's stop-loss halts it
 * for today: which group, what matched, that it sends again tomorrow while
 * the other groups keep sending. Resume now (kill permission) overrides it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { PauseOctagon, Play, SlidersHorizontal } from 'lucide-react';
import type { DropGenGroupSendStatus } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import type { AutoSendLayer } from '../settings/autosend-draft';
import { useAutoSendMutations } from '../use-autosend-mutations';
import { groupLayer } from './group-rows';

/** The server's reason as a sentence — it may not end with a full stop. */
const sentence = (reason: string | null) => (reason && !/[.!?]$/.test(reason) ? `${reason}.` : reason);

export const GroupHaltBanner: FC<{
  countryKey: string;
  group: DropGenGroupSendStatus;
  onSettings: (layer: AutoSendLayer) => void;
}> = ({ countryKey, group, onSettings }) => {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const { resumeLayer } = useAutoSendMutations(countryKey);
  const resume = async () => {
    try {
      await resumeLayer.mutateAsync(group.masterId);
      success(`${group.name} sends again — its stop-loss does not halt it again today`);
    } catch (err) {
      error(`${group.name} stays halted`, getApiErrorMessage(err));
    }
  };
  return (
    <div role="status" className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-3.5 animate-in fade-in-0 slide-in-from-top-1">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning/20 text-warning"><PauseOctagon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold">Auto-send of {group.name} halted for today — its stop-loss rule matched</p>
        <p className="mt-0.5 text-muted-foreground">
          {sentence(group.config.haltReason)} It sends again tomorrow; today its drafts wait for you. The other campaign groups keep sending.
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {hasPermission('drop_generator.kill') ? (
            <Button size="sm" variant="outline" loading={resumeLayer.isPending} onClick={() => void resume()}><Play className="mr-1 h-3.5 w-3.5" />Resume now</Button>
          ) : (
            <span className="text-xs text-muted-foreground">Resuming needs the kill permission.</span>
          )}
          <Button size="sm" variant="ghost" onClick={() => onSettings(groupLayer(group))}><SlidersHorizontal className="mr-1 h-3.5 w-3.5" />Edit the rule</Button>
        </div>
      </div>
    </div>
  );
};
